import { formatPercent } from 'components/Widget/shared/percent';
import type { CalculatorField, CalculatorFieldValues } from 'components/Widget/shared/types';
import type { RateType, RateTypeOption, SavingsSegment, TermUnit, TermUnitOption } from './types';

export const TITLE = 'Loan Calculator';

export const MONTHS_PER_YEAR = 12;

const RATE_TYPE_HINTS: Readonly<Record<RateType, string>> = {
  interestRate: 'The percentage a lender charges every year to borrow the money, before fees.',
  apr: "APR stands for Annual Percentage Rate. It's the total yearly cost of your loan, including interest plus fees, shown as one percentage annually.",
};

export const RATE_TYPE_OPTIONS: readonly RateTypeOption[] = [
  { key: 'interestRate', label: 'Interest Rate', hint: RATE_TYPE_HINTS.interestRate },
  { key: 'apr', label: 'APR', hint: RATE_TYPE_HINTS.apr },
];

export const TERM_UNIT_OPTIONS: readonly TermUnitOption[] = [
  { key: 'months', label: 'Months', monthsPerUnit: 1 },
  { key: 'years', label: 'Years', monthsPerUnit: MONTHS_PER_YEAR },
];

// A closed set with a default, so a key that misses is a coding error rather than user input.
export const getTermUnit = (key: TermUnit): TermUnitOption => {
  const option = TERM_UNIT_OPTIONS.find(unit => unit.key === key);

  if (!option) throw new Error(`Unsupported term unit: ${key}`);

  return option;
};

export const getRateType = (key: RateType): RateTypeOption => {
  const option = RATE_TYPE_OPTIONS.find(rate => rate.key === key);

  if (!option) throw new Error(`Unsupported rate type: ${key}`);

  return option;
};

/** Final copy from Legal, received 2026-09-18. */
export const DISCLAIMER =
  'This calculator is for educational purposes only and is not intended to offer any tax, legal, financial, or investment advice.';

/** The toggle reads "Extra payment (optional)"; the revealed field states the cadence it is paid on. */
export const EXTRA_PAYMENT_INPUT_LABEL = 'Additional monthly payment';

export const EXTRA_PAYMENT_SUFFIX = '/ month';

export const EXTRA_PAYMENT_FIELD: CalculatorField = {
  label: 'Extra payment',
  hint: 'An additional amount you pay toward your loan balance each month that can help you pay off the loan faster and with less interest.',
};

// Form defaults
const DEFAULT_LOAN_AMOUNT = '1,000';

const DEFAULT_RATE = 6;

const DEFAULT_TERM = 6;

export const DEFAULT_RATE_TYPE: RateTypeOption['key'] = 'interestRate';

export const DEFAULT_TERM_UNIT: TermUnitOption['key'] = 'months';

export const LOAN_AMOUNT_FIELD: CalculatorField = { label: 'Loan Amount', placeholder: DEFAULT_LOAN_AMOUNT };

export const RATE_TYPE_LEGEND = 'Rate type';

export const TERM_UNIT_LEGEND = 'Loan term unit';

export const LOAN_TERM_FIELD: CalculatorField = { label: 'Loan Term', placeholder: String(DEFAULT_TERM) };

/** The label is unused - the rate field takes its own from the rate-type radio - but the default is shared. */
export const RATE_FIELD: CalculatorField = { label: 'Rate', placeholder: formatPercent(DEFAULT_RATE) };

/** Matches the APY calculator's ceiling; a product-set bound is still open. */
export const MAX_FIELD_VALUE = 999999;

export const MAX_LOAN_AMOUNT = 10000000;

export const MAX_RATE = 100;

const MAX_TERM_MONTHS = 600;

const MAX_TERM_YEARS = 50;

/**
 * A one-month loan is a single closing payment, so an extra payment provably cannot save anything
 * and the comparison renders two identical columns. Two months is the shortest term the comparison
 * can say something true about. In years the floor stays 1, since a one-year loan is twelve
 * payments and an ordinary product.
 */
export const TERM_BOUNDS: Readonly<Record<TermUnit, { minimum: number; maximum: number }>> = {
  months: { minimum: 2, maximum: MAX_TERM_MONTHS },
  years: { minimum: 1, maximum: MAX_TERM_YEARS },
};

export const LOAN_AMOUNT_ERROR = 'Enter an amount between 1 and 10,000,000';

export const RATE_ERROR = 'Enter a rate between 0% and 100%';

/** Both bounds depend on the unit radio, so unlike its siblings this message cannot be a fixed string. */
export const TERM_ERROR = (termUnit: TermUnit): string => {
  const { minimum, maximum } = TERM_BOUNDS[termUnit];

  return `Enter a term between ${minimum} and ${maximum} ${termUnit}`;
};

export const EXTRA_PAYMENT_ERROR = 'Enter an amount between 1 and 999,999';

export const FORM_LABEL = 'Loan calculator';

export const INITIAL_CURRENCY_VALUES: CalculatorFieldValues = {
  [LOAN_AMOUNT_FIELD.label]: '',
  [EXTRA_PAYMENT_FIELD.label]: '',
};

export const INITIAL_TERM_VALUES: CalculatorFieldValues = { [LOAN_TERM_FIELD.label]: '' };

export const INITIAL_RATE_VALUES: CalculatorFieldValues = { [RATE_FIELD.label]: '' };

/** The teaser models this share of the scheduled payment when the user has not entered an extra one. */
export const DEFAULT_EXTRA_PAYMENT_RATE = 0.05;

/** Below a decade the modelled 5% saves too little to pitch unprompted; a figure the user entered is shown at any term. */
export const MIN_TEASER_TERM_MONTHS = 10 * MONTHS_PER_YEAR;

/**
 * A 50-year term is past anything the field accepts, so reaching this means the balance stopped
 * falling and the schedule would never terminate.
 */
export const MAX_MONTHS = 600;

/**
 * Rounded to one decimal rather than truncated: a 49-month payoff is 4.1 years, and flooring it to
 * 4 would understate the payoff beside a `monthsSaved` figure that counts every month.
 */
const formatYears = (months: number): string => {
  const years = Math.round((months / MONTHS_PER_YEAR) * 10) / 10;

  return `${years} ${years === 1 ? 'year' : 'years'}`;
};

/**
 * A year or less reads in months: the teaser's default extra clears only a handful of them, and
 * `0.3 years` sooner is a figure no borrower would act on.
 */
export const formatDuration = (months: number): string => {
  if (months <= MONTHS_PER_YEAR) return `${months} ${months === 1 ? 'month' : 'months'}`;

  return formatYears(months);
};

export const CURRENT_PLAN_HEADING = 'Current plan';

export const WITH_EXTRA_PAYMENT_HEADING = 'With extra payment';

/**
 * The three figures the logic doc names, in its order. `years` reads `monthsToPayoff` because the
 * schedule is settled in months, and `formatDuration` picks the unit the value is legible in.
 */
export const COMPARISON_ROWS = [
  { key: 'totalInterest', label: 'Est. total interest paid' },
  { key: 'years', label: 'Time to pay off' },
  { key: 'totalCost', label: 'Est. total cost of loan' },
] as const;

/** Omit the payoff clause when no whole month is saved; "0 months sooner" misstates the result. */
const savingsTail = (interestSaved: string, monthsSaved: number): readonly SavingsSegment[] => [
  { text: `est. ${interestSaved}`, isEmphasized: true },
  { text: ' in interest' },
  ...(monthsSaved > 0
    ? [{ text: ' and pay off your loan ' }, { text: `${formatDuration(monthsSaved)} sooner`, isEmphasized: true }]
    : []),
  { text: '.' },
];

export const RESULT_COPY = {
  heroEyebrow: 'Estimated monthly payment',
  /** Suffixed onto the switch label so the figure being modelled is never a mystery. */
  comparisonToggleAmount: (amount: string) => `${amount}/mo`,
  teaser: (extra: string, interestSaved: string, monthsSaved: number): readonly SavingsSegment[] => [
    { text: 'Paying an extra ' },
    { text: `${extra}/mo`, isEmphasized: true },
    { text: ' could save you an ' },
    ...savingsTail(interestSaved, monthsSaved),
  ],
  /**
   * Carries the payoff saving because the pills do not: they state the two durations side by side,
   * leaving the difference between them for the reader to work out.
   */
  comparison: (extra: string, interestSaved: string, monthsSaved: number): readonly SavingsSegment[] => [
    { text: 'By adding ' },
    { text: extra, isEmphasized: true },
    { text: " to your monthly payment, you'll save an " },
    ...savingsTail(interestSaved, monthsSaved),
  ],
} as const;

/** Self-contained copy for the live-region announcement; the panel itself is too long to hear out. */
export const SUMMARY_COPY = {
  payment: (monthlyPayment: string, payoff: string) =>
    `Your estimated monthly payment is ${monthlyPayment}, paying the loan off in ${payoff}.`,
} as const;

/**
 * A decade of yearly ticks is the most an axis this width can label without the years colliding;
 * past that they step in fives, which keeps a 50-year term at ten labels rather than fifty.
 */
export const YEARLY_TICK_MAX_MONTHS = 120;

export const TICK_YEARS_WHEN_SPARSE = 5;

/**
 * Under a year there is no whole year to label, so the axis switches unit rather than going bare -
 * the same threshold `formatDuration` reads the payoff in months at.
 */
export const MONTHLY_TICK_MAX_MONTHS = MONTHS_PER_YEAR;

/** Twelve monthly labels would collide in this panel; stepping in quarters caps a sub-year axis at four. */
export const TICK_MONTHS_WHEN_SPARSE = 3;

/**
 * A quarterly tick landing this close to the payoff tick would overlap it, so the regular one gives
 * way - the payoff is the figure the reader came for.
 */
export const TICK_MONTHS_MIN_GAP = 1;

export const CHART_TICK_COUNT = 4;

/** Dashed rather than colour alone: a line chart has no adjacency to distinguish the two lines by. */
export const CHART_STROKES = {
  baseline: { className: 'stroke-accent-green-600', dash: undefined },
  withExtra: { className: 'stroke-accent-green-400', dash: '6 4' },
} as const;

export const CHART_COPY = {
  title: 'Est. remaining balance over time',
  legend: [
    { key: 'baseline', label: 'Current plan' },
    { key: 'withExtra', label: 'With extra payment' },
  ] satisfies readonly { key: keyof typeof CHART_STROKES; label: string }[],
  /** States the shape, not the figures: the pills already carry every number, point by point. */
  label: (payoff: string, opening: string) =>
    `Line chart: remaining loan balance over ${payoff}, falling from ${opening} to zero.`,
  comparisonLabel: (payoff: string, opening: string, withExtraPayoff: string) =>
    `Line chart: remaining loan balance over ${payoff}, falling from ${opening} to zero, compared with ${withExtraPayoff} when paying extra.`,
  yearTick: (year: number) => `${year} yr`,
  monthTick: (month: number) => `${month} mo`,
} as const;
